// Collect Eligibility: the single engine deciding whether an item may be
// collected in store, and which reason blocks it. See CONTEXT.md.

export type CollectBlockReason = 'nonCncProduct' | 'engraved' | 'specialOrder' | 'resizedForYou' | 'outOfStock'

export type CollectItemView = {
  availableForClickAndCollect: boolean
  isEngraved: boolean
  isSpecialOrder: boolean
  isResized: boolean
}

export type CollectRule = {
  reason: CollectBlockReason
  blocks: (view: CollectItemView) => boolean
}

// The availability gate (nonCncProduct) and stock (outOfStock) live per surface,
// not here — the surfaces differ on them.
export const LEGACY_ITEM_RULES: CollectRule[] = [
  { reason: 'engraved', blocks: (v) => v.isEngraved },
  { reason: 'specialOrder', blocks: (v) => v.isSpecialOrder },
  { reason: 'resizedForYou', blocks: (v) => v.isResized },
]

// Under BOPIS no item type blocks collection — engraved/special-order/resized
// items ship to store from the DC, so type only steers lead-time messaging.
export const BOPIS_ITEM_RULES: CollectRule[] = []

export const itemRulesForSwitch = (bopisEnabled: boolean): CollectRule[] =>
  bopisEnabled ? BOPIS_ITEM_RULES : LEGACY_ITEM_RULES

export const firstItemBlock = (
  view: CollectItemView,
  rules: CollectRule[] = LEGACY_ITEM_RULES,
): CollectBlockReason | null => rules.find((rule) => rule.blocks(view))?.reason ?? null

// Cart gates on the C&C availability flag before the shared item rules; the
// product page treats availability as messaging, not a block.
export const cartItemBlock = (view: CollectItemView, rules?: CollectRule[]): CollectBlockReason | null =>
  !view.availableForClickAndCollect ? 'nonCncProduct' : firstItemBlock(view, rules)

// NOTE: checks the cart item, not the PDP product.
export const isResizedForYouProduct = (product?: ProductItemWithProductData) => {
  const ringSize = product?.c_ringSize
  const coreSize = product?.productCustomAttributes?.c_coreSize
  const specialOrderSku = !!product?.c_specialOrderSKU

  return ringSize && ringSize !== coreSize && !specialOrderSku
}

export const isSpecialOrderItem = (item: ProductItemWithProductData) => {
  return !!item?.c_specialOrderSKU
}

export const collectViewFromCartItem = (item: ProductItemWithProductData): CollectItemView => ({
  availableForClickAndCollect: !!item?.productCustomAttributes?.c_availableForClickAndCollect,
  isEngraved: !!item?.c_engravingText?.trim(),
  isSpecialOrder: isSpecialOrderItem(item),
  isResized: !!isResizedForYouProduct(item),
})

// Product-page detection diverges on purpose: a special order only blocks when
// no resize is offered, and a resize only blocks when that size is out of stock.

export type PdpCollectFacts = {
  isEngraved: boolean
  isSpecialOrderProduct: boolean
  selectedSizeIsSpecialOrder: boolean
  resizeAvailable: boolean
  sizeInStock: boolean
  availableForClickAndCollect: boolean
}

export const collectViewFromPdp = (facts: PdpCollectFacts): CollectItemView => ({
  availableForClickAndCollect: facts.availableForClickAndCollect,
  isEngraved: facts.isEngraved,
  isSpecialOrder: (facts.selectedSizeIsSpecialOrder || facts.isSpecialOrderProduct) && !facts.resizeAvailable,
  isResized: facts.resizeAvailable && !facts.sizeInStock,
})

// Cart-line surface: the checkout/bag ProductCard. It already receives the
// engine's verdicts as props (isMto, isResized) plus the engraving text and the
// C&C flag, so the adapter just normalises them into the shared view.
export type CartLineBlockReason = CollectBlockReason | 'overCartQuantity'

export const collectViewFromCartLine = (facts: {
  availableForClickAndCollect: boolean
  engravingText?: string
  isSpecialOrder: boolean
  isResized: boolean
}): CollectItemView => ({
  availableForClickAndCollect: facts.availableForClickAndCollect,
  isEngraved: !!facts.engravingText?.trim(),
  isSpecialOrder: facts.isSpecialOrder,
  isResized: facts.isResized,
})

// Lines of a SKU that draw on its stock line, in basket order — engraved,
// resized and special-order lines are produced at the DC and place no demand
// on it. Feeds the store-first allocation (allocateStockLines).
export const stockConsumingLineIds = (cart: BasketWithProductData | undefined, productId?: string): string[] =>
  cart?.productItems
    ?.filter((item) => item.productId === productId)
    .filter((item) => {
      const view = collectViewFromCartItem(item)
      return !view.isEngraved && !view.isSpecialOrder && !view.isResized
    })
    .map((item) => item.itemId ?? '') ?? []

// Cart line gates on the shared item rules, then applies its own stock checks.
// Stock sits outside the rule set (per surface): `stock` is null until the store
// stock query resolves, so stock-based reasons hold back until then.
export const cartLineBlock = (
  view: CollectItemView,
  stock: { storeQuantity: number; quantityInCart: number } | null,
): CartLineBlockReason | null => {
  const itemBlock = cartItemBlock(view)
  if (itemBlock) return itemBlock
  if (!stock) return null
  if (stock.storeQuantity <= 0) return 'outOfStock'
  if (stock.quantityInCart > stock.storeQuantity) return 'overCartQuantity'
  return null
}

// How the cart-line surface renders each block reason.
export const CART_LINE_UNAVAILABLE_MESSAGE_KEY = {
  nonCncProduct: 'checkout.product_card.click_collect_unavailable',
  engraved: 'checkout.product_card.click_collect_unavailable_engraved',
  specialOrder: 'checkout.product_card.click_collect_size_unavailable',
  resizedForYou: 'checkout.product_card.click_collect_size_unavailable',
  outOfStock: 'checkout.product_card.product_unavailable',
  overCartQuantity: 'checkout.product_card.product_stock_unavailable',
} satisfies Record<CartLineBlockReason, string>

// Store-line surface: the checkout/bag find-in-store list, one row per cart line
// at a given store. Same rules and stock checks as the cart line — it reuses
// cartLineBlock — with the store's own C&C gate folded into availability so a
// store that doesn't offer click & collect reads as nonCncProduct.
export const collectViewFromStoreLine = (
  item: ProductItemWithProductData,
  storeClickAndCollectEnabled: boolean,
): CollectItemView => {
  const view = collectViewFromCartItem(item)
  return { ...view, availableForClickAndCollect: view.availableForClickAndCollect && storeClickAndCollectEnabled }
}

// This surface has no distinct copy for the stock reasons, so outOfStock and
// overCartQuantity both fall back to the generic not-available line.
export const STORE_LINE_MESSAGE_KEY = {
  nonCncProduct: 'product_details_page.find_store.popup.stock_message.not_available',
  engraved: 'product_details_page.find_store.popup.stock_message.engraved_not_available',
  specialOrder: 'product_details_page.find_store.popup.stock_message.size_not_available',
  resizedForYou: 'product_details_page.find_store.popup.stock_message.size_not_available',
  outOfStock: 'product_details_page.find_store.popup.stock_message.not_available',
  overCartQuantity: 'product_details_page.find_store.popup.stock_message.not_available',
} satisfies Record<CartLineBlockReason, string>
